import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import { Organisation } from '../../types/organization';
import {
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Paper,
  Container,
  Alert,
  IconButton,
  InputAdornment,
  StepIcon,
  StepConnector,
  styled,
  Fade,
  useMediaQuery,
  useTheme,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import logo from '../../assets/cacLogo.svg';
import { CognitoService } from '../../services/api/auth/cognito.service';
import Notifications from '../common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { useDispatch } from 'react-redux';
import ContactDeveloper from '../auth/ContactDeveloper';
import { createOrgUser, orgLongPolling } from '../services/ServiceOrgCreate';
import { AUTH_CONSTANTS, ROUTES } from '../../config/constants';
import { setSelectedOrganizationId } from '../../store/slices/organizationContextSlice';
import {
  fetchOrganiationsById,
  setSelectedOrganisation,
} from '../../store/slices/organizationSlice';
import { loginUser, setClientResourceId } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';
import LoadingAnimation from '../common/LoadingAnimation';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import ExpiredLinkView from './ExpiredLinkView';
import { userEndpoints } from '../../services/api/endpoints/user.endpoint';
import {
  clearActivationCredentials,
  getActivationCredentials,
  storeActivationCredentials,
} from './storedata';
import { CreateResourceDTO } from '../../types/resource';
import { resourceEndpoints } from '../../services/api/endpoints/resource.endpoint';
import { serviceEndpoints } from '../../services/api/endpoints/service.endpoint';
import { serviceMappingEndpoints } from '../../services/api/endpoints/serviceMapping.endpoint';
import ServicesView from '../../views/Services/ServicesView';
import WorkingHoursSettings, {
  WorkingHoursSettingsRef,
} from '../../views/Settings/WorkingHoursSettings';
import { cognitoOAuthEndpoint } from '../../services/api/endpoints/cognito-oauth.endpoint';
import GoogleSignInButton from '../auth/GoogleSignInButton';
import { Profession, PROFESSION_SERVICES } from '../../config/constants/professions';

enum ActivationStep {
  FETCH_ORGANIZATION = 0,
  ENTER_PASSWORD,
  CREATE_COGNITO_USER,
  CONFIRM_SIGNUP,
  CREATE_ORGANIZATION,
  COMPLETE,
  /* scheduler-only */
  SELECT_PROFESSION,
  ADD_WORKING_HOURS,
  ADD_SERVICES,
}

const stepLabels = [
  'Fetching Details',
  'Create Password',
  'Creating User',
  'Verification',
  'Creating your company profile, this may take a few moments',
  'Activation Complete',
  'Select Profession',
  'Set Working Hours',
  'Add Services',
];

const getVisibleLabels = (isScheduler: boolean, isOAuth: boolean = false) => {
  let labels = stepLabels;

  // Filter out scheduler-only steps if not scheduler
  if (!isScheduler) {
    labels = labels.filter(
      (_, idx) =>
        idx !== ActivationStep.SELECT_PROFESSION &&
        idx !== ActivationStep.ADD_WORKING_HOURS &&
        idx !== ActivationStep.ADD_SERVICES
    );
  }

  // Filter out password and verification steps for OAuth users
  if (isOAuth) {
    labels = labels.filter(
      (_, idx) =>
        idx !== ActivationStep.ENTER_PASSWORD &&
        idx !== ActivationStep.CREATE_COGNITO_USER &&
        idx !== ActivationStep.CONFIRM_SIGNUP
    );
  }

  return labels;
};

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: 20,
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  width: '100%',
  maxWidth: 800,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  border: '1px solid rgba(0, 0, 0, 0.06)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: 16,
    gap: theme.spacing(2),
    margin: theme.spacing(0, 2),
  },
}));

const StyledConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.divider,
    borderTopWidth: 2,
  },
  '&.Mui-active .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-completed .MuiStepConnector-line': {
    borderColor: theme.palette.primary.main,
  },
}));

const CustomStepIcon = styled(StepIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  '&.Mui-completed': {
    color: theme.palette.success.main,
  },
  '&.Mui-active': {
    color: theme.palette.primary.dark,
  },
}));

// Primary button style matching the app's design system
const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#5C6BC0',
  color: '#ffffff',
  borderRadius: '12px',
  padding: '12px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 14px 0 rgba(92, 107, 192, 0.39)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#3F51B5',
    boxShadow: '0 6px 20px 0 rgba(92, 107, 192, 0.5)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px 24px',
    fontSize: '0.9rem',
    width: '100%',
  },
}));

export default function ActivateView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Ref for WorkingHoursSettings to call save
  const workingHoursRef = useRef<WorkingHoursSettingsRef>(null);

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const [data, setData] = useState<Organisation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<ActivationStep>(ActivationStep.FETCH_ORGANIZATION);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [canResendCode, setCanResendCode] = useState(false);
  const [resendTimeout, setResendTimeout] = useState(60);
  const [cognitoId, setCognitoId] = useState('');
  const [showContactDeveloper, setShowContactDeveloper] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [linkExpired, setLinkExpired] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);
  const [wrongAccountError, setWrongAccountError] = useState(false);
  const [expectedEmail, setExpectedEmail] = useState('');
  const [savingWorkingHours, setSavingWorkingHours] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState<Profession | ''>('');
  const [customProfessionName, setCustomProfessionName] = useState('');
  const [savingProfession, setSavingProfession] = useState(false);
  const [isDirectSignup, setIsDirectSignup] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  // Detect if user arrived via direct redirect from signup
  useEffect(() => {
    const signupFlow = sessionStorage.getItem('signup_flow');
    const signupIsOAuth = sessionStorage.getItem('signup_is_oauth');
    if (signupFlow === 'direct') {
      setIsDirectSignup(true);
      if (signupIsOAuth === 'true') {
        setIsOAuthUser(true);
      }
      // Clean up sessionStorage
      sessionStorage.removeItem('signup_flow');
      sessionStorage.removeItem('signup_is_oauth');
    }
  }, []);

  const isScheduler = data?.[0]?.isBookingEnabled ?? false;
  const visibleLabels = getVisibleLabels(isScheduler, isOAuthUser);

  // Password validation
  useEffect(() => {
    const errors = [];
    if (password) {
      if (password.length < 8) errors.push('Password must be at least 8 characters');
      if (!/[A-Z]/.test(password)) errors.push('Include at least one uppercase letter');
      if (!/[0-9]/.test(password)) errors.push('Include at least one number');
      if (!/[!@#$%^&*()_\-+=<>?{}[\]~]/.test(password))
        errors.push('Include at least one special character (!@#$%^&*()_\-+=<>?{}[\]~)');
    }
    setPasswordErrors(errors);
  }, [password]);

  const passwordsValid =
    password.length >= 8 &&
    password === confirmPassword &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*()_\-+=<>?{}[\]~]/.test(password);

  const fetchOrganization = async () => {
    try {
      console.log('fetchOrganization started, id:', id);
      if (!id) {
        throw new Error('Organization ID is missing');
      }

      const response: Organisation[] = await organizationEndpoints.getActiveteOrganisations(id);
      console.log('getActiveteOrganisations response:', response);
      const organization = response?.[0];

      if (!organization) {
        setLoading(false);
        setLinkExpired(true);
        return;
      }

      // Handle organization status checks first
      if (organization.status === 'active') {
        setLoading(false);
        setCurrentStep(ActivationStep.COMPLETE);
        dispatch(setSelectedOrganizationId(id));
        dispatch(setSelectedOrganisation(organization));
        return;
      }

      if (organization.status === 'failed_creation') {
        setLoading(false);
        setShowContactDeveloper(true);
        return;
      }

      const adminEmail = organization.client.email;

      if (!adminEmail) {
        throw new Error('No email found for the admin');
      }

      // Check for OAuth user using our new endpoint
      let oauthCheck = null;
      try {
        console.log('Checking OAuth user for email:', adminEmail);
        oauthCheck = await cognitoOAuthEndpoint.checkOAuthUser(adminEmail);
        console.log('OAuth check response:', oauthCheck);
      } catch (oauthErr) {
        // OAuth check endpoint might not be deployed yet - continue with regular flow
        console.warn('OAuth check failed, continuing with regular flow:', oauthErr);
      }

      // Handle OAuth users (Google Sign-In, etc.)
      if (oauthCheck?.exists && oauthCheck?.isOAuth) {
        console.log('OAuth user detected, checking if logged in with correct account');
        setIsOAuthUser(true);
        setData(response);

        // Check if the currently logged-in user matches the organization admin email
        let isLoggedIn = false;
        try {
          const currentUser = await CognitoService.getCurrentUser();
          const currentUserEmail = currentUser?.user?.email?.toLowerCase();

          if (currentUserEmail) {
            isLoggedIn = true;
            if (adminEmail.toLowerCase() !== currentUserEmail) {
              // Wrong account - show error and prompt to sign out
              console.log('Email mismatch:', { expected: adminEmail, current: currentUserEmail });
              setWrongAccountError(true);
              setExpectedEmail(adminEmail);
              setLoading(false);
              return;
            }
          }
        } catch (userCheckError) {
          // User is not logged in
          console.log('No user logged in, will need to sign in');
          isLoggedIn = false;
        }

        // If OAuth user is not logged in, show sign-in screen
        if (!isLoggedIn) {
          console.log('OAuth user needs to sign in first');
          setExpectedEmail(adminEmail);
          setLoading(false);
          // Stay on current step - the UI will show Google Sign-In button
          return;
        }

        // OAuth users skip password and verification steps
        // They're already authenticated via Google
        if (oauthCheck.username) {
          setCognitoId(oauthCheck.username);
        }

        // Skip directly to organization creation
        // Don't set loading to false here - let handleCreateOrganization keep loading state
        setCurrentStep(ActivationStep.CREATE_ORGANIZATION);
        // Return without going to finally block (loading stays true)
        return;
      }

      const cognitoUserResponse = await userEndpoints.getCognitoUser(adminEmail);

      // Handle regular cognito user statuses
      switch (true) {
        case cognitoUserResponse.userStatus === 'NOT_FOUND' && !cognitoUserResponse.emailVerified:
          setData(response);
          setLoading(false); // Show password form
          setCurrentStep(ActivationStep.ENTER_PASSWORD);
          break;
        case cognitoUserResponse.userStatus === 'UNCONFIRMED' && !cognitoUserResponse.emailVerified:
          setData(response);
          setLoading(false); // Show confirmation form
          setCurrentStep(ActivationStep.CONFIRM_SIGNUP);
          break;
        case cognitoUserResponse.userStatus === 'CONFIRMED' && cognitoUserResponse.emailVerified:
          setData(response);
          if (oauthCheck?.username) {
            setCognitoId(oauthCheck.username);
          }
          setIsExistingUser(true);
          setLoading(false);
          setCurrentStep(ActivationStep.ENTER_PASSWORD);
          break;
        // Handle EXTERNAL_PROVIDER status (federated identity)
        case cognitoUserResponse.userStatus === 'EXTERNAL_PROVIDER':
          setIsOAuthUser(true);
          setData(response);
          // Keep loading true - CREATE_ORGANIZATION will handle it
          setCurrentStep(ActivationStep.CREATE_ORGANIZATION);
          break;
        default:
          throw new Error('Unexpected user status from Cognito');
      }
    } catch (err: any) {
      console.error('fetchOrganization error:', err);
      setNotification({
        open: true,
        message: err.message || 'Error fetching organization details',
        severity: SEVERITY.ERROR,
      });
      // Only set loading false on error - successful transitions keep loading true
      setLoading(false);
    }
  };

  const handleCreateCognitoUser = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.client.email;
      if (!adminEmail) throw new Error('No email found for the admin');

      const userSignup = await CognitoService.signUp({
        email: adminEmail,
        password: password,
        orgId: id!,
        role: 'client_admin',
      });
      storeActivationCredentials(adminEmail, password, userSignup.userId!);
      setCognitoId(userSignup.userId!);
      setCurrentStep(ActivationStep.CONFIRM_SIGNUP);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error creating admin user',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCode = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.client.email;
      if (!adminEmail) throw new Error('No email found for confirmation');

      await CognitoService.confirmCode(adminEmail, confirmationCode);
      await dispatch(loginUser({ email: adminEmail, password }));

      setCurrentStep(ActivationStep.CREATE_ORGANIZATION);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error confirming signup',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.client.email;
      if (!adminEmail) throw new Error('No email found for resending code');

      if (isExistingUser) {
        await CognitoService.forgetPassword(adminEmail);
      } else {
        await CognitoService.resendSignUpCode(adminEmail);
      }

      setNotification({
        open: true,
        message: 'Verification code sent to your email',
        severity: SEVERITY.SUCCESS,
      });

      // Reset countdown
      setCanResendCode(false);
      setResendTimeout(60);

      let countdown = 60;
      const timer = setInterval(() => {
        countdown -= 1;
        setResendTimeout(countdown);
        if (countdown <= 0) {
          clearInterval(timer);
          setCanResendCode(true);
        }
      }, 1000);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error resending confirmation code',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      setLoading(true);
      const clientEmail = data?.[0]?.client.email;
      const clientFirstName = data?.[0]?.client.firstName;
      if (!clientEmail || !clientFirstName || !id) throw new Error('Organization data is missing');
      // invoke org create sqs
      await organizationEndpoints.invokeOrganisationCreation(id!);
      // long polling for orgCreate
      const isActive = await orgLongPolling(id);

      if (!isActive) {
        setShowContactDeveloper(true);
        return;
      }

      // create user in db
      // Use public endpoint for OAuth users or existing users whose token has a different clientId
      const usePublicEndpoint = isOAuthUser || isExistingUser;
      await createOrgUser(
        {
          orgId: id,
          email: clientEmail,
          cognitoId: cognitoId,
          firstName: clientFirstName,
          role: AUTH_CONSTANTS.ROLES.ADMIN,
          status: 'active',
          hasSeenTour: false,
        },
        usePublicEndpoint
      );

      // Force a token refresh to get the updated custom attributes (clientId, role)
      // The backend just updated custom:clientId and custom:role in Cognito
      if (isOAuthUser || isExistingUser) {
        try {
          // Clear the cached token first so the next API call gets a fresh one
          const { tokenService } = await import('../../services/api/auth/token.service');
          tokenService.clearToken();

          const cognitoService = new CognitoService();
          await cognitoService.getCurrentSession(); // This forces a token refresh
          console.log('Token refreshed with updated Cognito attributes');
        } catch (refreshError) {
          console.warn('Could not refresh token:', refreshError);
          // Continue anyway - user may need to re-login
        }
      }

      const scheduler = data?.[0]?.isBookingEnabled;
      if (scheduler) {
        try {
          const resourceData: CreateResourceDTO = {
            name: clientFirstName,
            email: clientEmail,
            isDashboardEnabled: false,
            defaultDuration: 60,
            bookingsPerSession: 1,
            timezone: 'Australia/Sydney',
          };

          // Note: This requires authentication, so it may fail for OAuth users
          // They can create their resource later in the dashboard
          const resource = await resourceEndpoints.createResource(id, resourceData);

          if (resource?.id) {
            dispatch(setClientResourceId(resource.id));
          }
        } catch (resourceError) {
          // Log but don't fail the activation - user can create resource later
          console.warn('Could not create initial resource during activation:', resourceError);
        }
      }
      dispatch(setSelectedOrganizationId(id!));
      await dispatch(fetchOrganiationsById(id!));
      if (scheduler) {
        setCurrentStep(ActivationStep.SELECT_PROFESSION);
      } else {
        setCurrentStep(ActivationStep.COMPLETE);
      }
    } catch (err: any) {
      setShowContactDeveloper(true);
      setNotification({
        open: true,
        message: err.message || 'Error creating organization',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUserPasswordReset = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.client.email;
      if (!adminEmail) throw new Error('No email found');

      await CognitoService.forgetPassword(adminEmail);

      setNotification({
        open: true,
        message: 'Verification code sent to your email',
        severity: SEVERITY.SUCCESS,
      });
      setCurrentStep(ActivationStep.CONFIRM_SIGNUP);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error sending verification code',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUserConfirmCode = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.client.email;
      if (!adminEmail) throw new Error('No email found');

      await CognitoService.confirmNewPassword({
        email: adminEmail,
        code: confirmationCode,
        newPassword: password,
      });

      await dispatch(loginUser({ email: adminEmail, password }));
      setCurrentStep(ActivationStep.CREATE_ORGANIZATION);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error confirming password. Please check your code.',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === ActivationStep.ENTER_PASSWORD && isExistingUser) {
      handleExistingUserPasswordReset();
    } else if (currentStep === ActivationStep.ENTER_PASSWORD && passwordsValid) {
      setCurrentStep(ActivationStep.CREATE_COGNITO_USER);
    }
  };

  useEffect(() => {
    if (currentStep === ActivationStep.FETCH_ORGANIZATION) {
      fetchOrganization();
    } else if (currentStep === ActivationStep.CREATE_COGNITO_USER) {
      handleCreateCognitoUser();
    } else if (currentStep === ActivationStep.CREATE_ORGANIZATION) {
      handleCreateOrganization();
    } else if (currentStep === ActivationStep.CONFIRM_SIGNUP) {
      setCanResendCode(false);
      setResendTimeout(60);

      let countdown = 60;
      const timer = setInterval(() => {
        countdown -= 1;
        setResendTimeout(countdown);
        if (countdown <= 0) {
          clearInterval(timer);
          setCanResendCode(true);
        }
      }, 1000);

      return () => clearInterval(timer);
    } else if (currentStep === ActivationStep.COMPLETE) {
      clearActivationCredentials();
      const timer = setTimeout(() => {
        navigate(ROUTES.DASHBOARD);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    // Load stored credentials if they exist
    const storedCredentials = getActivationCredentials();
    if (storedCredentials && data?.[0]?.client.email === storedCredentials.email) {
      setPassword(storedCredentials.password);
      setConfirmPassword(storedCredentials.password);
      setCognitoId(storedCredentials.cognitoId);
    }
  }, [data]);

  const handleProfessionSubmit = async () => {
    if (!selectedProfession || !id) return;
    if (selectedProfession === Profession.OTHERS && !customProfessionName.trim()) return;

    try {
      setSavingProfession(true);

      const professionLabel =
        selectedProfession === Profession.OTHERS ? customProfessionName.trim() : selectedProfession;

      // Update organisation's resourceProfessional field — exclude key/read-only attributes
      const org = data?.[0];
      const {
        id: _id,
        status: _status,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        expiryDate: _exp,
        clientEmail: _ce,
        ...updatableOrg
      } = org as any;
      await organizationEndpoints.updateOrganisation(id, {
        ...updatableOrg,
        resourceProfessional: professionLabel,
      } as Partial<Organisation>);

      // Auto-create predefined services if not "Others"
      if (selectedProfession !== Profession.OTHERS) {
        const services = PROFESSION_SERVICES[selectedProfession] || [];
        const createdServices = await Promise.all(
          services.map(svc =>
            serviceEndpoints
              .createService(id, {
                name: svc.name,
                duration: svc.duration,
                description: svc.description,
                isAddonService: false,
                isNewUserService: false,
              })
              .catch(err => {
                console.warn(`Failed to create service "${svc.name}":`, err);
                return null;
              })
          )
        );

        // Create service-resource mappings with the resource created during org setup
        try {
          const resources = await resourceEndpoints.listResources(id);
          const resource = resources?.[0];
          if (resource?.id) {
            const validServices = createdServices.filter(Boolean);
            await Promise.allSettled(
              validServices.map(svc =>
                svc
                  ? serviceMappingEndpoints.createMapping(id, svc.id, resource.id)
                  : Promise.resolve()
              )
            );
          }
        } catch (mappingError) {
          console.warn('Could not create service-resource mappings:', mappingError);
        }
      }

      // Re-fetch org into Redux so WorkingHoursSettings picks up the updated resourceProfessional
      await dispatch(fetchOrganiationsById(id));

      setCurrentStep(ActivationStep.ADD_WORKING_HOURS);
    } catch (error) {
      console.error('Failed to save profession:', error);
      setNotification({
        open: true,
        message: 'Failed to save profession. Please try again.',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setSavingProfession(false);
    }
  };

  const handleSignOutAndRetry = async () => {
    try {
      await CognitoService.signOut();
      localStorage.clear();
      // Reload the page to start fresh
      window.location.reload();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return linkExpired ? (
    <ExpiredLinkView />
  ) : showContactDeveloper ? (
    <ContactDeveloper message="We encountered an issue setting up your organization. Our team has been notified and will contact you shortly." />
  ) : wrongAccountError ? (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: 2,
            borderRadius: '16px',
            backgroundColor: 'rgba(43, 23, 172, 0.08)',
            mb: 3,
          }}
        >
          <img src={logo} alt="CAC_logo" style={{ width: '80px' }} />
        </Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Wrong Google Account
        </Typography>
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            borderRadius: '12px',
            maxWidth: 400,
          }}
        >
          You're signed in with a different Google account. Please sign out and sign in with{' '}
          <strong>{expectedEmail}</strong> to continue activation.
        </Alert>
        <PrimaryButton onClick={handleSignOutAndRetry}>Sign Out & Try Again</PrimaryButton>
      </Box>
    </Container>
  ) : (
    <Container maxWidth="lg" disableGutters>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          py: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 2 },
          gap: { xs: 2, sm: 4 },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 }, px: { xs: 2, sm: 0 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '16px',
              backgroundColor: 'rgba(43, 23, 172, 0.08)',
              mb: 2,
            }}
          >
            <img
              src={logo}
              alt="CAC_logo"
              style={{
                width: isMobile ? '64px' : '80px',
              }}
            />
          </Box>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            fontWeight={700}
            gutterBottom
            sx={{ mb: 1, color: 'text.primary' }}
          >
            Welcome to CACXI
          </Typography>
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            color="text.secondary"
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            Let's get your organization set up in just a few steps
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            mb: { xs: 2, sm: 4 },
            overflowX: 'auto',
            px: { xs: 1, sm: 0 },
          }}
        >
          <Stepper
            activeStep={visibleLabels.indexOf(stepLabels[currentStep])}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            connector={<StyledConnector />}
            sx={{
              minWidth: isMobile ? 'auto' : 'fit-content',
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
            }}
          >
            {visibleLabels.map((label, _index) => (
              <Step key={label}>
                <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Fade in={true} timeout={500}>
          <StyledPaper elevation={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <LoadingAnimation message={`${stepLabels[currentStep]}...`} />
              </Box>
            ) : (
              <>
                {/* OAuth user needs to sign in with Google */}
                {isOAuthUser && currentStep === ActivationStep.FETCH_ORGANIZATION && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        Sign In to Continue
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please sign in with your Google account to complete your organization setup.
                      </Typography>
                    </Box>

                    {expectedEmail && (
                      <Alert
                        severity="info"
                        sx={{
                          mb: 3,
                          borderRadius: '12px',
                          textAlign: 'left',
                        }}
                      >
                        Sign in with: <strong>{expectedEmail}</strong>
                      </Alert>
                    )}

                    <GoogleSignInButton
                      mode="signin"
                      onBeforeRedirect={() => {
                        // Store the activation URL so we can return here after OAuth
                        sessionStorage.setItem('oauth_return_url', window.location.pathname);
                      }}
                    />
                  </Box>
                )}

                {currentStep === ActivationStep.ENTER_PASSWORD && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        {isExistingUser ? 'Set Your Password' : 'Create Your Password'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isExistingUser
                          ? 'Create a new password for your account'
                          : 'Secure your admin account with a strong password'}
                      </Typography>
                    </Box>

                    <TextField
                      label="Email Address"
                      type="email"
                      defaultValue={data?.[0]?.client.email}
                      disabled
                      fullWidth
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      fullWidth
                      variant="outlined"
                      error={password.length > 0 && passwordErrors.length > 0}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {password.length > 0 && passwordErrors.length > 0 && (
                      <Box sx={{ mt: -1 }}>
                        {passwordErrors.map((error, index) => (
                          <Typography key={index} variant="caption" color="error" display="block">
                            • {error}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <TextField
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      fullWidth
                      variant="outlined"
                      error={confirmPassword.length > 0 && password !== confirmPassword}
                      helperText={
                        confirmPassword.length > 0 && password !== confirmPassword
                          ? 'Passwords do not match'
                          : ''
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        mt: 3,
                      }}
                    >
                      <PrimaryButton onClick={handleNext} disabled={!passwordsValid}>
                        Continue
                      </PrimaryButton>
                    </Box>
                  </>
                )}

                {currentStep === ActivationStep.CONFIRM_SIGNUP && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        Verify Your Email
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enter the 6-digit code we sent to your email
                      </Typography>
                    </Box>

                    <Alert
                      severity="info"
                      sx={{
                        mb: 2,
                        borderRadius: '12px',
                        '& .MuiAlert-icon': {
                          alignItems: 'center',
                        },
                      }}
                    >
                      Code sent to <strong>{data?.[0]?.client.email}</strong>
                    </Alert>

                    <TextField
                      type="text"
                      label="Verification Code"
                      value={confirmationCode}
                      onChange={e => setConfirmationCode(e.target.value)}
                      fullWidth
                      placeholder="Enter the 6-digit code"
                      variant="outlined"
                      inputProps={{
                        maxLength: 6,
                        style: { letterSpacing: '0.5em', textAlign: 'center' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '1.25rem',
                          fontWeight: 600,
                        },
                      }}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 2, sm: 0 },
                        mt: 3,
                      }}
                    >
                      <Button
                        onClick={handleResendCode}
                        disabled={!canResendCode || loading}
                        sx={{
                          textTransform: 'none',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            color: 'primary.main',
                          },
                        }}
                      >
                        {canResendCode ? 'Resend code' : `Resend in ${resendTimeout}s`}
                      </Button>

                      <PrimaryButton
                        onClick={isExistingUser ? handleExistingUserConfirmCode : handleConfirmCode}
                        disabled={confirmationCode.length < 6}
                      >
                        Verify Email
                      </PrimaryButton>
                    </Box>
                  </>
                )}

                {currentStep === ActivationStep.SELECT_PROFESSION && (
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        What's Your Profession?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select your profession to auto-populate relevant services for your business.
                      </Typography>
                    </Box>

                    <RadioGroup
                      value={selectedProfession}
                      onChange={e => setSelectedProfession(e.target.value as Profession)}
                    >
                      {Object.values(Profession).map(profession => (
                        <FormControlLabel
                          key={profession}
                          value={profession}
                          control={<Radio />}
                          label={profession}
                          sx={{
                            mb: 0.5,
                            '& .MuiFormControlLabel-label': { fontSize: '0.95rem' },
                          }}
                        />
                      ))}
                    </RadioGroup>

                    {selectedProfession === Profession.OTHERS && (
                      <TextField
                        label="Your Profession Name"
                        value={customProfessionName}
                        onChange={e => setCustomProfessionName(e.target.value)}
                        fullWidth
                        variant="outlined"
                        placeholder="e.g. Carpenter, Painter..."
                        sx={{
                          mt: 2,
                          '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        pt: 3,
                        borderTop: 1,
                        borderColor: 'divider',
                        mt: 3,
                      }}
                    >
                      <PrimaryButton
                        onClick={handleProfessionSubmit}
                        disabled={
                          savingProfession ||
                          !selectedProfession ||
                          (selectedProfession === Profession.OTHERS && !customProfessionName.trim())
                        }
                        startIcon={
                          savingProfession ? <CircularProgress size={20} color="inherit" /> : null
                        }
                      >
                        {savingProfession ? 'Setting up...' : 'Save & Continue'}
                      </PrimaryButton>
                    </Box>
                  </Box>
                )}

                {currentStep === ActivationStep.ADD_WORKING_HOURS && (
                  <Box sx={{ width: '100%' }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        Set Your Working Hours
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Define when your business is open for bookings. You can adjust this anytime
                        in Settings.
                      </Typography>
                    </Box>

                    {/* Hours component */}
                    <Box sx={{ overflowX: 'auto', mb: 3 }}>
                      <WorkingHoursSettings ref={workingHoursRef} hideSaveButton />
                    </Box>

                    {/* Action bar */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        pt: 3,
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <PrimaryButton
                        onClick={async () => {
                          try {
                            setSavingWorkingHours(true);
                            // Save working hours first, then continue
                            if (workingHoursRef.current) {
                              await workingHoursRef.current.save();
                            }
                            setCurrentStep(ActivationStep.ADD_SERVICES);
                          } catch (error) {
                            console.error('Failed to save working hours:', error);
                            setNotification({
                              open: true,
                              message: 'Failed to save working hours. Please try again.',
                              severity: SEVERITY.ERROR,
                            });
                          } finally {
                            setSavingWorkingHours(false);
                          }
                        }}
                        disabled={savingWorkingHours}
                        startIcon={
                          savingWorkingHours ? <CircularProgress size={20} color="inherit" /> : null
                        }
                      >
                        {savingWorkingHours ? 'Saving...' : 'Save & Continue'}
                      </PrimaryButton>
                    </Box>
                  </Box>
                )}

                {currentStep === ActivationStep.ADD_SERVICES && (
                  <Box sx={{ width: '100%' }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        Add Your First Service
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        To complete your setup, please add at least one service. This is required
                        before you can start accepting bookings.
                      </Typography>
                    </Box>

                    {/* Services component */}
                    <Box sx={{ overflowX: 'auto', mb: 3 }}>
                      <ServicesView isActivation />
                    </Box>

                    {/* Action bar */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                        pt: 3,
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <PrimaryButton onClick={() => setCurrentStep(ActivationStep.COMPLETE)}>
                        Complete Setup
                      </PrimaryButton>
                    </Box>
                  </Box>
                )}

                {currentStep === ActivationStep.COMPLETE && (
                  <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                    <CheckCircleOutlineIcon
                      sx={{
                        fontSize: { xs: 72, sm: 96 },
                        mb: 2,
                        color: '#10b981',
                      }}
                    />
                    <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom fontWeight="bold">
                      You're All Set!
                    </Typography>
                    <Typography
                      variant={isMobile ? 'body2' : 'body1'}
                      color="text.secondary"
                      sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}
                    >
                      Your organization has been successfully activated. Redirecting to your
                      dashboard...
                    </Typography>
                    <PrimaryButton onClick={() => navigate('/dashboard')}>
                      Go to Dashboard
                    </PrimaryButton>
                  </Box>
                )}

                {currentStep === ActivationStep.FETCH_ORGANIZATION && (
                  <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: '12px',
                        '& .MuiAlert-icon': {
                          fontSize: '24px',
                        },
                      }}
                    >
                      Failed to load organization details. Please try again.
                    </Alert>
                    <PrimaryButton
                      onClick={() => {
                        setLoading(true);
                        fetchOrganization();
                      }}
                    >
                      Try Again
                    </PrimaryButton>
                  </Box>
                )}
              </>
            )}
          </StyledPaper>
        </Fade>

        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </Box>
    </Container>
  );
}
